import React, { useState, useEffect } from "react";
import "./ScheduleAuctionView.scss";
import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ActionMenuIcon from "../ActionMenuIcon";
import TextField from "@mui/material/TextField";
import AuctionPostConfiguration from "./AuctionPostConfiguration";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import { CommonEnums } from "../../../../../enums/common.enums";
import { PropertyApi } from "../../../../../utils/api/Property";
import { CommonHelpers } from "../../../../../utils/helpers/common.helpers";
import ProtectedComponent from "../../../../../components/ProtectedComponent";
import NavigationLayout from "../../../navigation";
import { clientRoutes } from "../../../../../utils/routes";
import BackArrowIcon from "../../../../../components/icons/BackArrowIcon";
import AuctionviewMenuIcon from "./AuctionviewMenuIcon";
import Loading from "../../../../loading/Loading";
import PreviewModal from "../../propertypost/view/PreviewModal";
import PostAssets from "./PostAssets";

dayjs.extend(utc);

const BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const validationSchema = Yup.object().shape({
  auctioneer_name: Yup.string().optional(),
  auction_date: Yup.date().optional(),
  auction_start_time: Yup.string().optional(),
  auction_end_time: Yup.string().optional(),
});

const ScheduleAuctionView = () => {
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("auction_info");
  // CommonEnums.scheduleAuction.create_auction.key
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const step = searchParams.get("step") || "create_auction";
  const [timeTo, setTimeTo] = useState("00:00");
  const [showScheduleForm, setShowScheduleForm] = useState(true);
  const [localPostStatus, setLocalPostStatus] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<{
    id: string;
    file: string;
    tags: string[];
    address: string;
    timeFrom: dayjs.Dayjs | null;
    timeTo: dayjs.Dayjs | null;
    price: string;
    listed: string;
    auctionCreated: string;
    auctionUpdated: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [postType, setPostType] = useState<string | null>(null);
  const [postStatus, setPostStatus] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedButton, setSelectedButton] = useState("schedule");
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [scheduleDatetime, setScheduleDatetime] = useState<string | null>(null);
  const [auctionPost, setAuctionPost] = useState<any | null>(null);

  const [date, setDate] = useState("");
  const formik = useFormik({
    initialValues: {
      auctioneer_name: "",
      auction_date: null as dayjs.Dayjs | null,
      auction_start_time: null as dayjs.Dayjs | null,
      auction_end_time: null as dayjs.Dayjs | null,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const formattedData = {
          auctioneer_name: values.auctioneer_name,
          auction_date: values.auction_date
            ? values.auction_date.toISOString()
            : null,
          auction_start_time: values.auction_start_time
            ? values.auction_start_time.toISOString()
            : null,
          auction_end_time: values.auction_end_time
            ? values.auction_end_time.toISOString()
            : null,
        };

        localStorage.setItem("auctionData", JSON.stringify(formattedData));

        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          // next.set("step", CommonEnums.scheduleAuction.configure_post.key);
          return next;
        });
      } catch (err: any) {
        console.error("Error saving auction locally:", err);
        toast.error("Failed to save auction");
      }
    },
  });

  const handleUpdatePostConfiguration = async (data: {
    postType: string;
    scheduleDatetime: string;
    auctioneerName?: string | null;
    auctionDate?: string | null;
    auctionStartTime?: string | null;
    auctionEndTime?: string | null;
  }) => {
    if (!scheduleId) return;

    setIsUpdating(true);
    try {
      const formattedData = {
        post_type: data.postType,
        schedule_datetime: data.scheduleDatetime,
        auctioneer_name: data.auctioneerName,
        auction_date: data.auctionDate,
        auction_start_time: data.auctionStartTime,
        auction_end_time: data.auctionEndTime,
      };

      const res = await PropertyApi.updateAuctionPost(
        formattedData,
        scheduleId
      );

      if (res?.data?.success) {
        toast.success("Auction updated successfully!");
        // dispatch(propertieAction?.setInvitesListReset());
      } else {
        toast.error(
          res?.data?.message || "Failed to update post configuration"
        );
      }
    } catch (err: any) {
      console.error("Error updating post configuration:", err);
      toast.error("Failed to update post configuration");
    }
    setIsUpdating(false);
  };

  const fetchAuctionSchedule = async () => {
    if (!scheduleId) return;

    setLoading(true);
    try {
      const res = await PropertyApi.getAuctionScheduleById(scheduleId);

      if (res?.data?.success && res.data.data) {
        const schedule = res.data.data;
        const {
          auction_date,
          auctioneer_name,
          property_id,
          auction_start_time,
          auction_end_time,
          createdAt,
          updatedAt,
          post_type,
          post_status,
          schedule_datetime,
        } = res.data.data;

        setAuctionPost(schedule);

        let selectedActivity: any = null;
        if (post_type === "image_post") {
          selectedActivity = schedule?.image_post_design_activity_ref;
        } else if (post_type === "story_post") {
          selectedActivity = schedule?.story_post_design_activity_ref;
        } else if (post_type === "video_post") {
          selectedActivity = schedule?.video_post_design_activity_ref;
        } else if (post_type === "reel_post") {
          selectedActivity = schedule?.reel_post_design_activity_ref;
        }

        const images: string[] = selectedActivity?.design_task_id
          ?.output_image_path
          ? [
              CommonHelpers.getImageUrlFromImageTool({
                path: selectedActivity.design_task_id.output_image_path,
              }),
            ]
          : [];

        setPreviewImages(images);

        if (post_type) {
          setPostType(post_type);
        }

        if (schedule_datetime) {
          setScheduleDatetime(schedule_datetime);
        }

        if (post_status) {
          setPostStatus(post_status);
        }

        formik.setValues({
          auctioneer_name: auctioneer_name || "",
          auction_date: auction_date ? dayjs.utc(auction_date) : null,
          auction_start_time: auction_start_time
            ? dayjs(auction_start_time)
            : null,
          auction_end_time: auction_end_time ? dayjs(auction_end_time) : null,
        });

        if (property_id) {
          setSelectedProperty({
            id: property_id._id,
            file: property_id.thumbnail_image_ref?.compressed_file_path
              ? `${BASE_URL}/${property_id.thumbnail_image_ref.compressed_file_path}`
              : "/images/image-video-generation-error-fallback-image-for-card.png",
            tags: [
              property_id.property_stage_ref?.name,
              property_id.listing_type?.name,
              property_id.property_type_ref?.name,
            ].filter(Boolean),
            address:
              [
                property_id.property_no,
                property_id.property_no_and_street_name,
                property_id.city,
                property_id.state,
                property_id.post_code,
              ]
                .filter(Boolean)
                .join(", ") || "",
            timeFrom: auction_start_time ? dayjs(auction_start_time) : null,
            timeTo: auction_end_time ? dayjs(auction_end_time) : null,
            price:
              property_id.price_type?.slug === "price-range"
                ? `$${property_id.start_price} - $${property_id.end_price}`
                : property_id.price_type?.slug === "offer-above-xyz-price"
                ? `Offer above $${property_id.price}`
                : "Contact agent for price",
            listed: property_id.createdAt
              ? moment(property_id.createdAt).format("DD-MM-YYYY")
              : "",
            auctionCreated: createdAt
              ? moment(createdAt).format("hh:mm A, MMMM D, YYYY")
              : "",
            auctionUpdated: updatedAt
              ? moment(updatedAt).format("hh:mm A, MMMM D, YYYY")
              : "",
          });
        }
      }
    } catch (err) {
      console.error("Error fetching auction schedule:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAuctionSchedule();
  }, [scheduleId]);

     useEffect(() => {
        if (auctionPost?._id) {
          const currentParams: Record<string, string> = {};
    
          searchParams.forEach((value, key) => {
            currentParams[key] = value;
          });
    
          setSearchParams({
            ...currentParams,
            post_type: auctionPost.post_type || "",
            // subscription_type: propertyPost.subscription_type || "",
          });
        }
      }, [auctionPost, searchParams, setSearchParams]);

  useEffect(() => {
    if (scheduleDatetime) {
      const parsed = dayjs(scheduleDatetime);
      if (parsed.isValid()) {
        setDate(parsed.format("YYYY-MM-DD"));
        setTimeTo(parsed.format("HH:mm"));
      }
    }
  }, [scheduleDatetime]);

  function formatPostStatus(status?: string): string {
    if (!status) return "-";
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  const getPostStatusDisplay = (auctionPost: any) => {
    if (auctionPost?.post_status === "cancelled") {
      return "Cancelled";
    }

    const designActivityRef =
      auctionPost?.image_post_design_activity_ref ||
      auctionPost?.story_post_design_activity_ref ||
      auctionPost?.video_post_design_activity_ref ||
      auctionPost?.reel_post_design_activity_ref;

    const scheduleDatetime = auctionPost?.schedule_datetime
      ? auctionPost.schedule_datetime.$date
        ? new Date(auctionPost.schedule_datetime.$date)
        : new Date(auctionPost.schedule_datetime)
      : null;

    if (scheduleDatetime) {
      return "Scheduled to Post";
    }

    if (!designActivityRef) {
      return formatPostStatus(auctionPost?.post_status || "-");
    }

    const designStatus = designActivityRef.design_status;

    if (designStatus === "pending_for_social_media_upload") {
      return "Pending Upload";
    } else if (designStatus === "processing") {
      return "Processing...";
    }

    return formatPostStatus(auctionPost?.post_status || "-");
  };

  const getStatusClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "completed";
      case "processing":
        return "processing";
      case "scheduled for post":
        return "scheduled";
      case "pending_for_social_media_upload":
        return "pending-upload";
      case "cancelled":
        return "cancelled";
      case "errored":
      case "error":
        return "errored";
      default:
        return "";
    }
  };

  const statusFromAPI = auctionPost?.post_status || "";

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="schedule-auction-view-container">
          <div
            className="propertie-view-back-btn"
            onClick={() => {
              const subscriptionType = searchParams.get("subscription_type");
              const backUrl = subscriptionType
                ? `${clientRoutes.AuctionList}?subscription_type=${subscriptionType}`
                : clientRoutes.AuctionList;

              navigate(backUrl);
            }}
          >
            <div className="back-icon">
              <BackArrowIcon />
            </div>
            <p className="bakc-text-contain">Back to Auction Schedules</p>
          </div>

          <div className="header-row">
            <h1 className="title">Auction Info</h1>
            <div className="action-menu-icon-wrapper">
              <AuctionviewMenuIcon
                postId={scheduleId ?? ""}
                onUpdated={() => fetchAuctionSchedule()}
                onPreview={() => {
                  if (previewImages.length > 0) {
                    setPreviewOpen(true);
                  } else {
                    toast.error("No generated designs yet");
                  }
                }}
                previewImageUrl={previewImages[0]}
              />
            </div>
          </div>

          <div className="auction-top-info">
            <div className="top-row">
              <div className="info-item">
                <label>Auction Id</label>
                <p>{scheduleId}</p>
              </div>
              <div className="info-item">
                <label>Status</label>
                <p className={`status-badge ${getStatusClass(statusFromAPI)}`}>
                  {getPostStatusDisplay(auctionPost)}
                </p>
              </div>
              <div className="info-item">
                <label>Agency</label>
                <p className="agent-name">
                  {auctionPost?.agency_ref?.agency_trading_name}
                </p>
              </div>
              <div className="info-item">
                <label>Agent</label>
                <p className="agent-name">
                  {auctionPost?.agent_ref?.principal_agent_first_name}{" "}
                  {auctionPost?.agent_ref?.principal_agent_last_name}
                </p>
              </div>
            </div>
            <div className="bottom-row">
              <div className="info-item">
                <label>Property</label>
                <span className="value property-link">
                  <a
                    href={`/properties/view/${auctionPost?.property_id?._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {CommonHelpers.getPropertyAddress({
                      no: auctionPost?.property_id?.property_no,
                      streetName:
                        auctionPost?.property_id?.property_no_and_street_name,
                      city: auctionPost?.property_id?.city,
                      state: auctionPost?.property_id?.state,
                      postCode: auctionPost?.property_id?.post_code,
                      country: auctionPost?.property_id?.country,
                    })}
                  </a>
                </span>
              </div>
              <div className="info-item">
                <label>Schedule Datetime</label>
                <p>
                  {formik.values.auction_date
                    ? formik.values.auction_date.format("hh:mm A, MMMM D, YYYY")
                    : "-"}
                </p>
              </div>
              <div className="info-item">
                <label>Created On</label>
                <p>{selectedProperty?.auctionCreated || "—"}</p>
              </div>
              <div className="info-item">
                <label>Last Updated On</label>
                <p>{selectedProperty?.auctionUpdated || "—"}</p>
              </div>
            </div>
          </div>

          <div className="custom-tabs">
            <div
              className={`tab-item ${
                activeTab === "auction_info" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("auction_info");
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  step: "auction_info",
                });
              }}
            >
              Auction Info
            </div>

            <div
              className={`tab-item ${
                activeTab === "post_assets" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("post_assets");
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  step: "post_assets",
                });
              }}
            >
              Post Assets
            </div>

            <div
              className={`tab-item ${
                activeTab === "postConfig" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("postConfig");
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  step: "configure_post",
                });
              }}
            >
              Post Configuration
            </div>
          </div>

          {/* <ScheduleAuctionTabs
            activeTab={step}
            onTabChange={(newStep) =>
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("step", newStep);
                return next;
              })
            }
            showPostType={false}
            useInfoForCreateAuction={true}
          /> */}

          {loading ? (
            <div className="loading-container">
              <Loading />
            </div>
          ) : (
            <>
              {activeTab === "auction_info" && (
                <div>
                  <p className="main-header">Auction Info</p>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="formRow">
                      <div className="formField">
                        <label className="label">Auctioneer Name*</label>
                        <input
                          className={`input actioneer-name ${
                            formik.touched.auctioneer_name &&
                            formik.errors.auctioneer_name
                              ? "error"
                              : ""
                          }`}
                          name="auctioneer_name"
                          placeholder="Enter Auctioneer Name"
                          value={formik.values.auctioneer_name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.auctioneer_name &&
                          formik.errors.auctioneer_name && (
                            <div className="error-message">
                              {formik.errors.auctioneer_name}
                            </div>
                          )}
                      </div>
                      <div className="formField">
                        <label className="label">Auction Date*</label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            format="DD/MM/YYYY"
                            value={formik.values.auction_date}
                            onChange={(value) =>
                              formik.setFieldValue("auction_date", value)
                            }
                            slotProps={{
                              textField: {
                                className: `mui-datepicker-input input ${
                                  formik.touched.auction_date &&
                                  formik.errors.auction_date
                                    ? "error"
                                    : ""
                                }`,
                                name: "auction_date",
                                sx: {
                                  input: {
                                    color: "#000",
                                    "&::placeholder": {
                                      color: "#333",
                                      opacity: 1,
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                        {formik.touched.auction_date &&
                          formik.errors.auction_date && (
                            <div className="error-message">
                              {formik.errors.auction_date}
                            </div>
                          )}
                      </div>
                    </div>

                    {selectedProperty && (
                      <div className="formRow">
                        <div className="formField">
                          <label className="label2">Auction Time From*</label>
                          <TimePicker
                            value={formik.values.auction_start_time}
                            onChange={(value) =>
                              formik.setFieldValue("auction_start_time", value)
                            }
                            format="hh:mm A"
                            slotProps={{
                              textField: {
                                className: `mui-datepicker-input ${
                                  formik.touched.auction_start_time &&
                                  formik.errors.auction_start_time
                                    ? "error"
                                    : ""
                                }`,
                                name: "auction_start_time",
                              },
                            }}
                          />
                          {formik.touched.auction_start_time &&
                            formik.errors.auction_start_time && (
                              <div className="error-message">
                                {formik.errors.auction_start_time}
                              </div>
                            )}
                        </div>
                        <div className="formField">
                          <label className="label2">Auction Time To*</label>
                          <TimePicker
                            value={formik.values.auction_end_time}
                            onChange={(value) =>
                              formik.setFieldValue("auction_end_time", value)
                            }
                            format="hh:mm A"
                            slotProps={{
                              textField: {
                                className: `mui-datepicker-input ${
                                  formik.touched.auction_end_time &&
                                  formik.errors.auction_end_time
                                    ? "error"
                                    : ""
                                }`,
                                name: "auction_end_time",
                              },
                            }}
                          />
                          {formik.touched.auction_end_time &&
                            formik.errors.auction_end_time && (
                              <div className="error-message">
                                {formik.errors.auction_end_time}
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </LocalizationProvider>

                  <div className="schedule-post-form">
                    <h3 className="section-title">Schedule Date & Time</h3>
                    <p>Select date and time to schedule post</p>

                    {localPostStatus === "scheduled" && date && timeTo && (
                      <div className="status-message scheduled">
                        <p>Scheduled for Automatic Posting.</p>
                        <p className="auto-post">
                          Will be published at{" "}
                          {dayjs(timeTo, "HH:mm").format("hh:mm A")},{" "}
                          {dayjs(date).format("DD-MM-YYYY")}.
                        </p>
                      </div>
                    )}

                    {localPostStatus === "published" && date && timeTo && (
                      <div className="status-message published">
                        <p className="auto-post">
                          Post is Completed and already published.
                        </p>
                        <p className="publish-message">
                          Published on{" "}
                          {dayjs(timeTo, "HH:mm").format("hh:mm A")},{" "}
                          {dayjs(date).format("DD-MM-YYYY")}.
                        </p>
                      </div>
                    )}

                    {localPostStatus !== "published" && (
                      <div className="button-group">
                        <button
                          className={`auto-post ${
                            selectedButton === "auto" ? "selected" : ""
                          }`}
                          onClick={() => {
                            if (!date || !timeTo) {
                              toast.error(
                                "Please select both date and time to schedule."
                              );
                              return;
                            }
                            setLocalPostStatus("scheduled");
                            setSelectedButton("auto");
                            setShowScheduleForm(false);
                          }}
                        >
                          Auto Post
                        </button>

                        <button
                          className={`confirm-btn ${
                            selectedButton === "postNow" ? "selected" : ""
                          }`}
                          onClick={() => {
                            if (!date || !timeTo) {
                              toast.error(
                                "Please select both date and time to publish."
                              );
                              return;
                            }
                            setLocalPostStatus("published");
                            setSelectedButton("postNow");
                            setShowScheduleForm(false);
                          }}
                        >
                          Post Now
                        </button>

                        <button
                          className={`schedule-btn ${
                            showScheduleForm || selectedButton === "schedule"
                              ? "active selected"
                              : ""
                          }`}
                          onClick={() => {
                            setShowScheduleForm(!showScheduleForm);
                            setSelectedButton("schedule");
                          }}
                        >
                          Schedule Post
                        </button>
                      </div>
                    )}

                    {showScheduleForm && (
                      <div className="datetime-row">
                        <div className="datetime-field">
                          <label>Date*</label>
                          <div className="input-icon-wrapper">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={date ? dayjs(date) : null}
                                onChange={(newValue) =>
                                  setDate(
                                    newValue
                                      ? newValue.format("YYYY-MM-DD")
                                      : ""
                                  )
                                }
                                format="DD/MM/YYYY"
                                slots={{ textField: TextField }}
                                slotProps={{
                                  textField: {
                                    variant: "outlined",
                                    size: "small",
                                    fullWidth: false,
                                    InputProps: {
                                      className: "input",
                                    },
                                    sx: {
                                      input: {
                                        color: "#000",
                                        "&::placeholder": {
                                          color: "#333",
                                          opacity: 1,
                                        },
                                      },
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </div>
                        </div>

                        <div className="datetime-field">
                          <label>Time*</label>
                          <div className="input-icon-wrapper">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <TimePicker
                                value={timeTo ? dayjs(timeTo, "HH:mm") : null}
                                onChange={(newValue) =>
                                  setTimeTo(
                                    newValue ? newValue.format("HH:mm") : ""
                                  )
                                }
                                ampm
                                format="hh:mm A"
                                slots={{ textField: TextField }}
                                slotProps={{
                                  textField: {
                                    variant: "outlined",
                                    size: "small",
                                    fullWidth: false,
                                    InputProps: {
                                      className: "input",
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "post_assets" && <PostAssets />}

              {activeTab === "postConfig" && (
                <AuctionPostConfiguration
                  scheduleId={scheduleId}
                  postType={postType}
                  postStatus={postStatus}
                  scheduleDatetime={scheduleDatetime}
                  auctionDate={formik.values.auction_date}
                  auctioneerName={formik.values.auctioneer_name}
                  auctionStartTime={formik.values.auction_start_time}
                  auctionEndTime={formik.values.auction_end_time}
                  properties={selectedProperty ? [selectedProperty] : []}
                  property={selectedProperty}
                  onUpdatePostConfiguration={handleUpdatePostConfiguration}
                  onPreviewImagesReady={(imgs) => setPreviewImages(imgs)}
                />
              )}
            </>
          )}
          {previewOpen && (
            <PreviewModal
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              images={previewImages}
            />
          )}
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default ScheduleAuctionView;
